// Servicio de Firestore - Funciones para interactuar con la base de datos
// Proporciona funciones reutilizables para CRUD de colecciones

import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  setDoc,
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp
} from 'firebase/firestore'
import { db } from './firebase.config'

/**
 * Obtener un documento por ID
 * @param {string} collectionName - Nombre de la colección
 * @param {string} docId - ID del documento
 * @returns {Promise<Object|null>} Documento o null si no existe
 */
export const getDocument = async (collectionName, docId) => {
  try {
    const docRef = doc(db, collectionName, docId)
    const docSnap = await getDoc(docRef)
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      }
    }
    return null
  } catch (error) {
    console.error(`Error al obtener documento de ${collectionName}:`, error)
    throw error
  }
}

/**
 * Obtener todos los documentos de una colección
 * @param {string} collectionName - Nombre de la colección
 * @param {Array} filters - Array de filtros (opcional)
 * @param {string} orderByField - Campo para ordenar (opcional)
 * @param {string} orderDirection - Dirección del orden: 'asc' o 'desc' (opcional)
 * @param {number} maxResults - Número máximo de resultados (opcional)
 * @returns {Promise<Array>} Array de documentos
 */
export const getCollection = async (
  collectionName, 
  filters = [], 
  orderByField = null, 
  orderDirection = 'asc',
  maxResults = null
) => {
  try {
    // Para productos, orders, warehouses y stock, si hay filtros y ordenamiento, hacer el ordenamiento en el cliente
    // para evitar índices compuestos
    const shouldSortInClient = (collectionName === 'products' || collectionName === 'orders' || collectionName === 'warehouses' || collectionName === 'stock' || collectionName === 'stockMovements') && filters.length > 0 && orderByField
    
    let q = collection(db, collectionName)
    
    // Aplicar filtros si existen
    if (filters.length > 0) {
      filters.forEach(filter => {
        q = query(q, where(filter.field, filter.operator, filter.value))
      })
    }
    
    // Aplicar ordenamiento solo si no hay filtros (para evitar índices compuestos)
    if (orderByField && !shouldSortInClient) {
      q = query(q, orderBy(orderByField, orderDirection))
    }
    
    // Aplicar límite si se especifica
    if (maxResults) {
      q = query(q, limit(maxResults))
    }
    
    const querySnapshot = await getDocs(q)
    const documents = []
    
    querySnapshot.forEach((doc) => {
      documents.push({
        id: doc.id,
        ...doc.data()
      })
    })
    
    // Ordenar en el cliente si es necesario
    if (shouldSortInClient && orderByField) {
      documents.sort((a, b) => {
        const aValue = a[orderByField]
        const bValue = b[orderByField]
        
        // Manejar Timestamps de Firestore
        if (aValue && aValue.toDate) {
          const aDate = aValue.toDate().getTime()
          const bDate = bValue && bValue.toDate ? bValue.toDate().getTime() : 0
          return orderDirection === 'asc' ? aDate - bDate : bDate - aDate
        }
        
        // Manejar números
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return orderDirection === 'asc' ? aValue - bValue : bValue - aValue
        }
        
        // Manejar strings
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return orderDirection === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue)
        }
        
        return 0
      })
    }
    
    return documents
  } catch (error) {
    console.error(`Error al obtener colección ${collectionName}:`, error)
    throw error
  }
}

/**
 * Crear un nuevo documento
 * @param {string} collectionName - Nombre de la colección
 * @param {Object} data - Datos del documento
 * @returns {Promise<string>} ID del documento creado
 */
export const createDocument = async (collectionName, data) => {
  try {
    // Agregar timestamp de creación
    const documentData = {
      ...data,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    }
    
    const docRef = await addDoc(collection(db, collectionName), documentData)
    return docRef.id
  } catch (error) {
    console.error(`Error al crear documento en ${collectionName}:`, error)
    throw error
  }
}

/**
 * Crear o reemplazar un documento con ID fijo (setDoc)
 * @param {string} collectionName - Nombre de la colección
 * @param {string} docId - ID del documento
 * @param {Object} data - Datos del documento
 * @param {Object} options - Opciones
 * @param {boolean} options.merge - Si debe mergear (default: true)
 * @returns {Promise<void>}
 */
export const setDocument = async (collectionName, docId, data, options = {}) => {
  try {
    const { merge = true } = options
    const docRef = doc(db, collectionName, docId)

    const documentData = removeUndefined({
      ...data,
      updatedAt: Timestamp.now()
    })

    await setDoc(docRef, documentData, { merge })
  } catch (error) {
    console.error(`Error al guardar documento en ${collectionName}:`, error)
    throw error
  }
}

/**
 * Limpiar valores undefined de un objeto
 * Firestore no acepta valores undefined
 * @param {Object} obj - Objeto a limpiar
 * @returns {Object} Objeto sin valores undefined
 */
const removeUndefined = (obj) => {
  const cleaned = {}
  for (const key in obj) {
    if (obj[key] !== undefined) {
      cleaned[key] = obj[key]
    }
  }
  return cleaned
}

/**
 * Actualizar un documento
 * @param {string} collectionName - Nombre de la colección
 * @param {string} docId - ID del documento
 * @param {Object} data - Datos a actualizar
 * @returns {Promise<void>}
 */
export const updateDocument = async (collectionName, docId, data) => {
  try {
    const docRef = doc(db, collectionName, docId)
    
    // Agregar timestamp de actualización y limpiar undefined
    const updateData = removeUndefined({
      ...data,
      updatedAt: Timestamp.now()
    })
    
    await updateDoc(docRef, updateData)
  } catch (error) {
    console.error(`Error al actualizar documento en ${collectionName}:`, error)
    throw error
  }
}

/**
 * Eliminar un documento
 * @param {string} collectionName - Nombre de la colección
 * @param {string} docId - ID del documento
 * @returns {Promise<void>}
 */
export const deleteDocument = async (collectionName, docId) => {
  try {
    const docRef = doc(db, collectionName, docId)
    await deleteDoc(docRef)
  } catch (error) {
    console.error(`Error al eliminar documento de ${collectionName}:`, error)
    throw error
  }
}

/**
 * Servicio específico para productos
 */
export const productsService = {
  /**
   * Obtener todos los productos
   * @param {Object} options - Opciones de consulta
   * @returns {Promise<Array>} Array de productos
   */
  getAll: async (options = {}) => {
    const {
      category = null, // Compatibilidad con datos antiguos
      categoryId = null, // Nuevo campo
      commercialProfile = null,
      minPrice = null,
      maxPrice = null,
      orderByField = 'createdAt',
      orderDirection = 'desc',
      limitResults = null
    } = options

    const filters = []
    
    // Filtrar por categoría (priorizar categoryId, luego category para compatibilidad)
    if (categoryId) {
      filters.push({ field: 'categoryId', operator: '==', value: categoryId })
    } else if (category) {
      filters.push({ field: 'category', operator: '==', value: category })
    }
    
    // Filtrar por precio mínimo si se especifica
    if (minPrice !== null) {
      filters.push({ field: 'price', operator: '>=', value: minPrice })
    }
    
    // Filtrar por precio máximo si se especifica
    if (maxPrice !== null) {
      filters.push({ field: 'price', operator: '<=', value: maxPrice })
    }

    if (commercialProfile?.rubro && commercialProfile.rubro !== 'muebles') {
      filters.push({ field: 'commercialProfile.rubro', operator: '==', value: commercialProfile.rubro })
      if (commercialProfile.subrubro) {
        filters.push({ field: 'commercialProfile.subrubro', operator: '==', value: commercialProfile.subrubro })
      }
    }
    
    return await getCollection('products', filters, orderByField, orderDirection, limitResults)
  },

  /**
   * Obtener un producto por ID
   * @param {string} productId - ID del producto
   * @returns {Promise<Object|null>} Producto o null
   */
  getById: async (productId) => {
    return await getDocument('products', productId)
  },

  /**
   * Crear un nuevo producto
   * @param {Object} productData - Datos del producto
   * @returns {Promise<string>} ID del producto creado
   */
  create: async (productData) => {
    return await createDocument('products', productData)
  },

  /**
   * Actualizar un producto
   * @param {string} productId - ID del producto
   * @param {Object} productData - Datos a actualizar
   * @returns {Promise<void>}
   */
  update: async (productId, productData) => {
    return await updateDocument('products', productId, productData)
  },

  /**
   * Actualizar precio de un producto con historial
   * @param {string} productId - ID del producto
   * @param {number} newPrice - Nuevo precio
   * @param {Object} updateInfo - Información de la actualización
   * @param {string} updateInfo.userId - ID del usuario
   * @param {string} updateInfo.userName - Nombre del usuario
   * @param {string} updateInfo.reason - Razón del cambio
   * @param {string} updateInfo.purchaseOrderId - ID de la orden de compra (opcional)
   * @param {string} updateInfo.notes - Notas adicionales (opcional)
   * @returns {Promise<void>}
   */
  updatePrice: async (productId, newPrice, updateInfo) => {
    try {
      // Obtener producto actual
      const product = await getDocument('products', productId)
      if (!product) {
        throw new Error('Producto no encontrado')
      }

      const previousPrice = product.price || 0
      const newPriceNum = parseFloat(newPrice)

      // Si el precio no cambió, no hacer nada
      if (previousPrice === newPriceNum) {
        return
      }

      // Obtener historial actual o inicializar
      const priceHistory = product.priceHistory || []

      // Agregar entrada al historial
      const historyEntry = {
        previousPrice,
        newPrice: newPriceNum,
        changedAt: Timestamp.now(),
        changedBy: updateInfo.userId || null,
        changedByName: updateInfo.userName || null,
        reason: updateInfo.reason || 'Actualización de precio',
        purchaseOrderId: updateInfo.purchaseOrderId || null,
        notes: updateInfo.notes || null
      }

      priceHistory.push(historyEntry)

      // Actualizar producto con nuevo precio e historial
      await updateDocument('products', productId, {
        price: newPriceNum,
        priceHistory
      })

      // Si el producto tiene variantes, actualizar también sus precios
      if (product.variants && product.variants.length > 0) {
        const updatedVariants = product.variants.map(variant => ({
          ...variant,
          price: newPriceNum // Actualizar precio de todas las variantes
        }))

        await updateDocument('products', productId, {
          variants: updatedVariants
        })
      }
    } catch (error) {
      console.error('Error actualizando precio del producto:', error)
      throw error
    }
  },

  /**
   * Revertir precio de un producto a una entrada anterior del historial
   * @param {string} productId - ID del producto
   * @param {number} historyIndex - Índice de la entrada del historial a revertir
   * @param {Object} updateInfo - Información de la reversión
   * @returns {Promise<void>}
   */
  revertPrice: async (productId, historyIndex, updateInfo) => {
    try {
      const product = await getDocument('products', productId)
      if (!product) {
        throw new Error('Producto no encontrado')
      }

      const priceHistory = product.priceHistory || []
      if (historyIndex < 0 || historyIndex >= priceHistory.length) {
        throw new Error('Índice de historial inválido')
      }

      const historyEntry = priceHistory[historyIndex]
      const previousPrice = product.price || 0
      const revertedPrice = historyEntry.previousPrice

      // Agregar entrada al historial indicando la reversión
      const revertEntry = {
        previousPrice,
        newPrice: revertedPrice,
        changedAt: Timestamp.now(),
        changedBy: updateInfo.userId || null,
        changedByName: updateInfo.userName || null,
        reason: `Reversión a precio anterior`,
        notes: updateInfo.notes || null,
        revertedFrom: historyIndex
      }

      priceHistory.push(revertEntry)

      // Actualizar precio
      await updateDocument('products', productId, {
        price: revertedPrice,
        priceHistory
      })

      // Actualizar variantes si existen
      if (product.variants && product.variants.length > 0) {
        const updatedVariants = product.variants.map(variant => ({
          ...variant,
          price: revertedPrice
        }))

        await updateDocument('products', productId, {
          variants: updatedVariants
        })
      }
    } catch (error) {
      console.error('Error revirtiendo precio del producto:', error)
      throw error
    }
  },

  /**
   * Eliminar un producto
   * @param {string} productId - ID del producto
   * @returns {Promise<void>}
   */
  delete: async (productId) => {
    return await deleteDocument('products', productId)
  }
}

